﻿import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import 'rxjs/add/operator/catch';
import { Animations } from '../common/animations';
import { DatePipe } from '@angular/common';

// APP 
import { EntityService } from '../../services/entity.service';
import { SharedService } from '../../services/shared.service';
import { HelperService } from '../../services/helper.service';
import { FoucsChangeService } from '../../services/focuschange.service';
import { FilesService } from 'app/services/files.service';
import { MIHDocumentType, MIHSubDocumentType, MIHUploadDocInfo, MIHDocument } from 'app/viewmodel';

@Component({
    selector: 'app-upload',
    templateUrl: './applicationupload.component.html',
    providers: [EntityService, HelperService],
    animations: [Animations.collapseInOut]
})
export class ApplicationUploadComponent implements OnInit {
    @Input('projectId') projectId: number;
    @Input('documents') documents: Array<MIHDocument>;
    @Input('appStatus') appStatus: string; 
    @Input('sortColumn') sortColumn: string;
    @Input('sortDirection') sortDirection: string;
    @Input('lockChanges') lockChanges: boolean;
    @Output() sortColumnEvent: EventEmitter<string> = new EventEmitter();
    @Output() sortDirectionEvent: EventEmitter<string> = new EventEmitter();
    @Output() refreshDocumentsEvent: EventEmitter<void> = new EventEmitter();
    @ViewChild('fileUpload') fileUpload: ElementRef;
    pageError: any = [];
    documentTypes: Array<MIHDocumentType> = [];
    subDocumentTypes: Array<MIHSubDocumentType> = [];
    selectedDocumentType: MIHDocumentType;
    selectedSubDocumentType: MIHSubDocumentType;
    uploadDocInfo: MIHUploadDocInfo;
    fileName: string;
    documentTypeRequiredError: boolean = false;
    subDocumentTypeRequiredError: boolean = false;
    fileNameRequiredError: boolean = false;
    displayDeleteConfirmation: boolean = false;
    deleteDocumentID: number = 0;
    fileExt: string = 'PDF, XLS, XLSX, DOC, DOCX, TIF, JPG, JPEG, GIF';
    maxSize: number = 4;
        
    constructor(private router: Router
        , private filesService: FilesService
        , private sharedService: SharedService
        , private datePipe: DatePipe
        , private foucsChangeService: FoucsChangeService) {
    }

    ngOnInit() {
        // Get Document Types
        this.getDocumentTypes();
    }

    getDocumentTypes() {
        this.filesService.getDocumentTypes(this.projectId)
            .subscribe(
                successResp => {
                    if (!successResp.Success) {
                        this.showError(successResp.StatusMessage);
                    }
                    else {
                        this.documentTypes = successResp.Result;
                    }
                },
                error => {
                    this.showError(error.ExceptionMessage);
                });
    }

    getSubDocumentTypes(documentType: MIHDocumentType) {
        this.filesService.getSubDocumentTypes(documentType)
            .subscribe(
                successResp => {
                    if (!successResp.Success) {
                        this.showError(successResp.StatusMessage);
                    }
                    else {
                        this.subDocumentTypes = successResp.Result;
                    }
                },
                error => {
                    this.showError(error.ExceptionMessage);
                });
    }

    onDocumentTypeChange(documentType: MIHDocumentType) {
        this.documentTypeRequiredError = false;
        this.selectedSubDocumentType = null;
        this.subDocumentTypeRequiredError = false;
        this.fileName = "";
        this.fileNameRequiredError = false;
        if (documentType) {
            this.getSubDocumentTypes(documentType);
        } else {
            this.subDocumentTypes = new Array<MIHSubDocumentType>();
        }
    }

    onSubDocumentTypeChange(subDocumentType: MIHSubDocumentType) {
        if (subDocumentType && subDocumentType.DropDownText == 'Other') {
            this.fileName = "";
            this.fileNameRequiredError = false;
        }
        this.subDocumentTypeRequiredError = false;
    }

    downloadDocument(documentId: number, documentName: string) {
        this.filesService.downloadDocument(documentId, documentName)
            .subscribe(async (data: Blob) => {
                if (data && data.type == 'application/octet-stream') {
                    if (window.navigator.msSaveOrOpenBlob)
                        window.navigator.msSaveBlob(data, documentName);
                    else {
                        var a = window.document.createElement("a");
                        a.href = window.URL.createObjectURL(data);
                        a.download = documentName;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                    }
                } else if (data && data.type == 'text/json') {
                    var errorMessage = await (new Response(data)).text();
                    if (errorMessage && errorMessage.length > 2) {
                        errorMessage = errorMessage.slice(1, -1);
                    }
                    // Display the specific failure.                
                    if (errorMessage == 'This document is no longer available. Please refresh page for an updated list of documents.') {
                        this.showErrorAlert(errorMessage);
                    }
                    else {
                        this.showError(errorMessage);
                    }
                }
            });
    }

    confirmDeletion(mihDocumentID: number) {
        this.closeError();
        this.deleteDocumentID = mihDocumentID;
        this.displayDeleteConfirmation = true;
        this.foucsChangeService.changeFocus(false);
    }

    cancelDocumentDeletion() {
        this.deleteDocumentID = 0;
        this.displayDeleteConfirmation = false;
        this.foucsChangeService.changeFocus(true);
    }

    deleteDocument() {
        this.closeError();
        let mihDocumentId = this.deleteDocumentID;
        this.deleteDocumentID = 0;
        this.displayDeleteConfirmation = false;
        this.filesService.deleteDocument(mihDocumentId)
            .subscribe(
                success => {
                    if (!success.Success) {
                        // Display the specific failure.                
                        if (success.StatusMessage == 'This document is no longer available. Please refresh page for an updated list of documents.' ||
                            success.StatusMessage == 'This application is no longer available for modifications. Please refresh page for an updated application details.') {
                            this.showErrorAlert(success.StatusMessage);
                        }
                        else {
                            this.showError(success.StatusMessage);
                        }
                    }
                    else {
                        this.refreshDocumentsEvent.emit();
                    }
                },
                error => {
                    if (error.status == 401)
                        this.router.navigateByUrl('/login?session=0');

                    this.showError(error.ExceptionMessage)
                });
    }

    ////////// Save Uploaded file section /////////
    uploadDocument() {
        this.pageError = []; 
        this.fileUpload.nativeElement.value = '';

        this.documentTypeRequiredError = !this.selectedDocumentType;
        this.subDocumentTypeRequiredError = !this.selectedSubDocumentType;

        if (this.selectedSubDocumentType && this.selectedSubDocumentType.DropDownText == 'Other' && this.fileName != null) {
            this.fileName = this.fileName.trim();          
         }
        this.fileNameRequiredError = this.selectedSubDocumentType && this.selectedSubDocumentType.DropDownText == 'Other' && !this.fileName ? true : false;
        
        if (this.documentTypeRequiredError || this.subDocumentTypeRequiredError || this.fileNameRequiredError) {
            return false;
        }
    }
        
    filesSelected(evt) {

        let files = evt.target.files;
        if (files.length > 0 && (!this.isValidFiles(files))) {
            return false;
        }

        this.uploadDocInfo = new MIHUploadDocInfo();
        if (files.length > 0) {

            this.uploadDocInfo.ProjectID = this.selectedDocumentType.ProjectID;
            this.uploadDocInfo.BldgID = this.selectedDocumentType.BldgID;
            this.uploadDocInfo.DocumentType = this.selectedSubDocumentType.DocumentType;
            this.uploadDocInfo.DocumentSubType = this.selectedSubDocumentType.DocumentSubType;
            this.uploadDocInfo.DocumentSubTypeName = this.selectedSubDocumentType.DropDownText;
            this.uploadDocInfo.PartyID = this.selectedSubDocumentType.PartyID;
            this.uploadDocInfo.PartyStagingID = this.selectedSubDocumentType.PartyStagingID;
            if (this.selectedSubDocumentType.DocumentSubType == 654)
                this.uploadDocInfo.OtherDocType = this.fileName;
            else
                this.uploadDocInfo.OtherDocType = '';

            let formData: FormData = new FormData();
            for (var j = 0; j < files.length; j++) {
                formData.append("file[]", files[j], files[j].name);
            }

            this.filesService.uploadMIHFile(formData, this.uploadDocInfo)
                .subscribe(
                    success => {
                        if (!success.Success) {
                            // Display the specific failure.                
                            if (success.StatusMessage == 'This application is no longer available for modifications. Please refresh page for an updated application details.') {
                                this.showErrorAlert(success.StatusMessage);
                            }
                            else {
                                this.showError(success.StatusMessage);
                            }
                        }
                        else {
                            this.refreshDocumentsEvent.emit();
                            this.selectedDocumentType = null;
                            this.onDocumentTypeChange(this.selectedDocumentType);
                        }
                    },
                    error => {
                        if (error.status == 401)
                        this.router.navigateByUrl('/login?session=0');

                        this.showError(error.ExceptionMessage)
                    })
        }
    }

    private isValidFiles(files) {

        var isValidFile = true;
        var extensions = (this.fileExt.split(',')).map(function (x) { return x.toLocaleUpperCase().trim() });   // [PDF,XLS,XLSX,DOC,DOCX,TIF,JPG,JPEG,GIF]
        for (var i = 0; i < files.length; i++) {
            var ext = files[i].name.toUpperCase().split('.').pop() || files[i].name;
            
            if (extensions.indexOf(ext) == -1) {
                isValidFile = false;
                this.showErrorAlert("The specified file " + files[i].name + " could not be uploaded. Only the following file types are allowed: PDF, Excel, Word, JPG, JPEG, TIF and GIF.");
            }
            // Check max file size
            if (!this.isValidFileSize(files[i])) {
                isValidFile = false;
            }
        }

        return isValidFile;
    }

    private isValidFileSize(file) {

        var isValidFileSize = true;
        var fileSizeinMB = file.size / (1024 * 1024);
        var size = Math.round(fileSizeinMB * 100) / 100; // convert upto 2 decimal place

        if (size > this.maxSize) {
            this.showErrorAlert(file.name + " exceeds the maximum upload size for this site. Maximum upload file size: " + this.maxSize + "MB.");
            isValidFileSize = false;
        }
        return isValidFileSize;
    }

    /*  ///Test
    saveCheckList(projectId) {
        this.filesService.uploadCheckListTemplate(projectId)
            .subscribe(
            success => {
                if (!success.Success) {            
                    if (success.StatusMessage == 'This application is no longer available for modifications. Please refresh page for an updated application details.') {
                        this.showErrorAlert(success.StatusMessage);
                    }
                    else {
                        this.showError(success.StatusMessage);
                    }
                }
                else {
                    this.refreshDocumentsEvent.emit();
                    this.selectedDocumentType = null;
                    this.onDocumentTypeChange(this.selectedDocumentType);
                }
            },
            error => {
                if (error.status == 401)
                    this.router.navigateByUrl('/login?session=0');

                this.showError(error.ExceptionMessage)
            })

    }
    */

    /////////////end Save Uploaded file section /////////////

    ///////////view list of file- getUploadDocsByProjectId/////////////
    formatDate(date) {
        var retDate = '';
        if (date != null) {
            retDate = this.datePipe.transform(date, 'MM/dd/yyyy');
        }
        return retDate;
    }

    sortData(columnName: string) {
        this.sortDirection = (this.sortColumn == columnName) ? (this.sortDirection == 'asc' ? 'desc' : 'asc') : 'desc';
        this.sortColumn = columnName;
        this.sortColumnEvent.emit(this.sortColumn);
        this.sortDirectionEvent.emit(this.sortDirection);        
    }

    ///////// end view file list //////////

    closeError() {
        this.pageError = [];
    }

    showError(msg) {
        this.pageError = [];
        this.pageError.push(this.sharedService.getCustomError(msg));
        this.sharedService.processError(msg);
    }

    showErrorAlert(msg) {
        this.pageError = [];
        this.pageError.push(msg);
        this.sharedService.processError(msg);
    }
}
